import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCallback } from "react";

export const SCOUT_WRITE_ACCESS_SWITCH_LABEL = "Allow Scout to change your data";

export const SCOUT_WRITE_ACCESS_INFO_TOOLTIP =
  "When on, Scout can make changes to your Encamp data using your own permissions. Turn off if you only want Scout to read.";

function getIndicatorTooltip(allowMutations: boolean): string {
  if (allowMutations) {
    return "Scout can change your Encamp data. Click to turn off.";
  }

  return "Scout is read-only. Click to let Scout change your Encamp data.";
}

function getIndicatorAriaLabel(allowMutations: boolean): string {
  if (allowMutations) {
    return "Scout can change your Encamp data. Click to turn off.";
  }

  return "Scout is read-only. Click to allow changes to your Encamp data.";
}

export type UseScoutWriteAccessControlInput = {
  allowMutations: boolean;
  onAllowMutationsChange: (next: boolean) => void;
  disabled?: boolean;
};

export function useScoutWriteAccessControl(input: UseScoutWriteAccessControlInput) {
  const { featureFlags } = useFeatureFlags();
  const isFeatureEnabled = Boolean(featureFlags?.[FeatureFlagKey.ScoutWriteAccess]);
  const disabled = input.disabled ?? false;

  const toggle = useCallback(() => {
    if (disabled) {
      return;
    }

    input.onAllowMutationsChange(!input.allowMutations);
  }, [disabled, input]);

  return {
    isFeatureEnabled,
    allowMutations: input.allowMutations,
    disabled,
    toggle,
    switchLabel: SCOUT_WRITE_ACCESS_SWITCH_LABEL,
    infoTooltip: SCOUT_WRITE_ACCESS_INFO_TOOLTIP,
    indicatorTooltip: getIndicatorTooltip(input.allowMutations),
    indicatorAriaLabel: getIndicatorAriaLabel(input.allowMutations),
  };
}
